import { getToken } from '@api/config';
import { UpdateUserStatusPayload } from '@api/userStatusApiHandler';
import Card from '@components/Atoms/Card';
import DefaultButton from '@components/Atoms/DefaultButton';
import FormButtonNew from '@components/FormButtonNew/FormButtonNew';
import Modal from '@components/Modal';
import { getCompanyDetails } from '@helpers/auth.helper';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { useUpdateUserStatus } from '@hooks/useUserStatus';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { Skeleton } from '@mui/material';
import { SanitizedText } from '@shared/atoms';
import { LoadingBackdrop } from '@shared/molecules';
import { WellbeingHubInstantHelp } from '@shared/organisms';
import classNames from 'classnames';
import { PublicCompanyDetails } from 'custom.d';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { getInstantHelpData } from 'store/Actions/InstantHelpAction';
import styles from './index.module.scss';

const IntroductionPage: React.FunctionComponent = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [getHelp, setGetHelp] = useState<boolean>(false);
  const instantHelpData = useAppSelector((state: RootState) => state.instantHelp);
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<PublicCompanyDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    mutate: mutateStatus,
    isPending: isPendingStatus,
    isSuccess: isSuccessStatus,
  } = useUpdateUserStatus(true);

  const token = getToken();
  let organizationName;
  let organizationLogo;

  useEffect(() => {
    const getCompanyData = async () => {
      const data = await getCompanyDetails();
      if (data) {
        const compDetails = {
          companyLogo: data.company_logo,
          companyName: data.company_name,
          companyUrl: data.company_url,
          hideHapstarLogo: false,
          id: data.id,
          signUpText: data.sign_up_text,
          loginText: data.login_text,
          shareThoughts: data.share_thoughts,
          platformName: null,
        };
        setCompanyDetails(compDetails);
      }
    };

    if (token && token !== null && token !== undefined) {
      getCompanyData();
    }
  }, [token]);

  useEffect(() => {
    async function getInstantData() {
      await setLoading(true);
      await dispatch(getInstantHelpData());

      setLoading(false);
    }

    if (!instantHelpData.isLoaded) {
      getInstantData();
    }
  }, [dispatch, instantHelpData.isLoaded]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const el = document.getElementById('showInstantHelp');
      if (el) {
        el.style.color = '#019df2';
        el.style.cursor = 'pointer';
        el.onclick = () => {
          if (companyConfigData?.data?.showInstantHelp) {
            setGetHelp(true);
          }
        };
      }
    }, 100);

    return () => {
      const el = document.getElementById('showInstantHelp');
      if (el) {
        el.onclick = null;
      }

      clearTimeout(timeout);
    };
  }, [companyConfigData?.data?.welcomeMessageText]);

  const updateUserStatus = async () => {
    try {
      setIsSubmitting(true);

      const data: UpdateUserStatusPayload = {
        isRegistered: true,
        isJoined: true,
        isEligibleForSpeakUpAlerts: null,
        isOnboardCompleted: null,
        isFirstShortStarCompleted: null,
      };

      await mutateStatus(data, {
        onSuccess: (status) => {
          if (status.success) {
            history.push('/onboarding-assessment');
          }
        },
      });
    } catch (error) {
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (companyDetails !== null && companyDetails.id !== 0) {
    organizationName = companyDetails.companyName && companyDetails.companyName;
    organizationLogo = companyDetails.companyLogo
      ? `${process.env.PUBLIC_URL}/images/logos/${companyDetails.companyLogo}`
      : undefined;
  }

  return (
    <div className={styles.hsIntroPageContainer}>
      {(isSubmitting || isPendingStatus) && <LoadingBackdrop text="Setting things up for you…" />}
      <AuthenticationLayout
        title="Introduction"
        organizationLogo={organizationLogo}
        organizationAltText={organizationName}
        hideHapstarLogoEnabled={companyConfigData.data.hideHapstarLogo}
      >
        <div className={styles.hsIntroPageSubContainer}>
          <Card>
            <div className={styles.hsIntroCardContainer}>
              {companyConfigData?.data?.welcomeMessageText &&
              companyConfigData?.data?.welcomeMessageText?.length > 0 ? (
                <SanitizedText content={companyConfigData?.data?.welcomeMessageText} />
              ) : (
                <>
                  <h1>
                    <span>Before you begin</span>
                  </h1>
                  <p>
                    Hapstar is a wellbeing platform designed to support personal development and
                    wellness. This platform does not provide medical advice, diagnosis, or treatment
                    and is not a substitute for professional healthcare.
                  </p>
                  <p>
                    If you are experiencing a mental health emergency, having thoughts of self-harm,
                    or are in immediate danger, this platform may not be appropriate for you.
                  </p>
                  <p>
                    We have compiled a list of{' '}
                    <span
                      onClick={() => {
                        if (companyConfigData?.data && companyConfigData?.data?.showInstantHelp) {
                          setGetHelp(true);
                        }
                      }}
                    >
                      emergency resources
                    </span>{' '}
                    providing access to immediate support if you feel your safety, or the safety of
                    those around you, is at risk. If this applies to you, we strongly recommend
                    contacting emergency services or a crisis helpline immediately and notifying
                    your medical provider of your circumstances.
                  </p>
                  <p>
                    If you are not in crisis and wish to use this platform for wellbeing support,
                    please continue below.
                  </p>
                  <p>
                    By clicking &quot;Continue&quot;, you acknowledge that you have read and
                    understood this information and that this platform is for wellbeing support
                    only, not medical care.
                  </p>
                </>
              )}

              <FormButtonNew
                onClick={updateUserStatus}
                color="primary"
                disabled={isSubmitting || isPendingStatus}
              >
                Continue
              </FormButtonNew>
            </div>
          </Card>
        </div>

        {getHelp && (
          <div className="container">
            <Modal
              open={getHelp}
              setOpen={setGetHelp}
              onTapBackgroundClose
              className={styles.modalRoot}
              overlay
            >
              <DefaultButton
                color="primary"
                type="button"
                className={styles.btnClose}
                onClick={() => setGetHelp(false)}
              >
                <div className={styles.btnCloseLabel}>Close</div>
                <i className={classNames('icon', 'icon-x', styles.btnCloseIcon)} />
              </DefaultButton>
              {instantHelpData.error && !loading && <p>Unable to fetch wellbeing data</p>}

              {loading && (
                <div className="container">
                  <div className={styles.loadingContainer}>
                    <Skeleton variant="rounded" width="50%" height={50} />

                    <div className={styles.loadingDesc}>
                      <Skeleton variant="rounded" width="100%" height={20} />
                      <Skeleton variant="rounded" width="100%" height={20} />
                      <Skeleton variant="rounded" width="100%" height={20} />
                    </div>
                    <Skeleton variant="rounded" width="100%" height="100vh" />
                  </div>
                </div>
              )}

              {!instantHelpData.loading && !loading && (
                <WellbeingHubInstantHelp
                  title="Wellbeing Hub"
                  subText={companyConfigData?.data?.instantHelpDescription || ''}
                  instantData={instantHelpData.data}
                  loading={instantHelpData.loading}
                />
              )}

              {instantHelpData.data.length === 0 && !instantHelpData.loading && !loading && (
                <div className={styles.noDataContainer}>
                  <h3>No data found</h3>
                </div>
              )}
            </Modal>
          </div>
        )}
      </AuthenticationLayout>
    </div>
  );
};

export default IntroductionPage;
